import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const movie = movies.find((m) => m.id === id);
  const article = articles.find((a) => a.id === id);

  const content = movie || article;

  if (!content) {
    return (
      <>
        <SEOHead title="内容未找到 - 哈哈影院" description="抱歉，您要查看的内容不存在或已被移除。" />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center py-20">
            <i className="ri-error-warning-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的内容不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isMovie = 'director' in content;
  const relatedMovies = isMovie
    ? movies.filter((m) => m.id !== content.id && m.category === content.category).slice(0, 4)
    : [];
  const relatedArticles = !isMovie
    ? articles.filter((a) => a.id !== content.id && a.category === content.category).slice(0, 3)
    : [];

  const pageTitle = isMovie
    ? `${content.title} - ${content.title}在线观看 | 哈哈影院高清免费`
    : `${content.title} - 哈哈影院`;
  const pageDesc = isMovie
    ? `哈哈影院提供${content.title}高清免费在线观看，${content.title}${content.year}年${content.region}${content.director ? `导演${content.director}` : ''}执导，${content.actors ? content.actors.slice(0, 3).join('、') : ''}主演，评分${content.rating}。${content.description.slice(0, 60)}`
    : content.summary;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={isMovie ? `哈哈影院,${content.title},${content.title}在线观看,${content.title}免费,${content.tags.join(',')}` : `${content.title},哈哈影院`}
        canonicalUrl={`https://hahacinema.com/article/${id}`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogImage={'coverImage' in content ? content.coverImage : undefined}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': isMovie ? 'Movie' : 'Article',
          name: content.title,
          description: isMovie ? content.description : content.summary,
          ...(isMovie
            ? {
                dateCreated: content.year,
                image: content.coverImage,
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: content.rating,
                  bestRating: '10',
                },
              }
            : {
                datePublished: content.publishDate,
                author: { '@type': 'Person', name: content.author },
                image: content.coverImage,
              }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <Breadcrumb />

        {isMovie ? (
          <>
            <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
              <img
                src={content.bannerImage}
                alt={content.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent"></div>
            </section>

            <section className="px-4 md:px-6 -mt-32 relative z-10">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
                  <img
                    src={content.coverImage}
                    alt={content.title}
                    title={`${content.title} - 哈哈影院高清免费在线观看`}
                    className="w-full rounded-lg"
                  />
                </div>
                <div className="flex-1 pt-2">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {content.title}
                  </h1>
                  <p className="text-foreground-500 text-sm mt-1">{content.originalTitle}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-sm font-bold whitespace-nowrap">
                      {content.rating}
                    </span>
                    <span className="text-sm text-foreground-500">{content.year}</span>
                    <span className="text-foreground-300">·</span>
                    <span className="text-sm text-foreground-500">{content.duration}</span>
                    <span className="text-foreground-300">·</span>
                    <span className="text-sm text-foreground-500">{content.region}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {content.tags.map((tag: string) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 space-y-1.5 text-sm text-foreground-700">
                    <p><strong className="text-foreground-900">导演：</strong>{content.director}</p>
                    <p><strong className="text-foreground-900">主演：</strong>{content.actors.join('、')}</p>
                    <p><strong className="text-foreground-900">播放量：</strong>{(content.views / 10000).toFixed(0)}万次</p>
                    <p><strong className="text-foreground-900">更新：</strong>{content.updateDate}</p>
                  </div>
                  <button className="mt-5 inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white rounded-full font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                    立即观看
                  </button>
                </div>
              </div>
            </section>

            <section className="px-4 md:px-6 py-8">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">剧情简介</h2>
              <p className="text-sm text-foreground-700 leading-relaxed max-w-3xl">{content.description}</p>
            </section>

            {relatedMovies.length > 0 && (
              <section className="px-4 md:px-6 py-8 bg-background-100">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop>
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </>
        ) : (
          <>
            <section className="px-4 md:px-6 py-8 max-w-4xl mx-auto w-full">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {content.title}
              </h1>
              <div className="flex items-center gap-2 mt-3 text-xs text-foreground-500">
                <span>{content.author}</span>
                <span className="text-foreground-300">·</span>
                <span>{content.publishDate}</span>
                <span className="text-foreground-300">·</span>
                <span>{content.views.toLocaleString()} 阅读</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {content.tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-6 rounded-lg overflow-hidden">
                <img
                  src={content.coverImage}
                  alt={content.title}
                  className="w-full object-cover"
                />
              </div>
              <article className="mt-8 prose max-w-none text-foreground-700 text-sm leading-relaxed space-y-4">
                <div dangerouslySetInnerHTML={{ __html: content.content }} />
              </article>
            </section>

            {relatedArticles.length > 0 && (
              <section className="px-4 md:px-6 py-8 bg-background-100">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">相关文章</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                  {relatedArticles.map((a) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.id}`}
                      className="group rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img src={a.coverImage} alt={a.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading text-sm font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">{a.title}</h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </>
  );
}